import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Clock, MapPin } from "lucide-react";
import crestoraLogo from "@/assets/crestora-logo.webp";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [desktopDropdown, setDesktopDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    setDesktopDropdown(null);
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobileMenuOpen]);

  const toggleDropdown = (name: string) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-espresso/98 backdrop-blur-md shadow-elevated"
            : "bg-espresso"
        }`}
        role="banner"
      >
        <div className="container mx-auto px-4 md:px-8">
          <nav className="flex items-center justify-between h-20" aria-label="Navigasi utama">
            <Link to="/" aria-label="Crestora Coffee Roasters - Beranda">
              <img src={crestoraLogo} alt="Logo Crestora Coffee Roasters" className="h-12 w-auto" width={48} height={48} />
            </Link>

            {/* Navigasi Desktop */}
            <div className="hidden lg:flex items-center gap-10">
              <Link to="/" className={`font-sans text-[13px] tracking-[0.2em] uppercase transition-colors ${location.pathname === "/" ? "text-crestora-gold" : "text-cream hover:text-crestora-gold"}`}>
                BERANDA
              </Link>

              <div className="relative group" onMouseEnter={() => setDesktopDropdown("about")} onMouseLeave={() => setDesktopDropdown(null)}>
                <button className={`font-sans text-[13px] tracking-[0.2em] uppercase transition-colors flex items-center gap-1 ${["/cerita-kami", "/faq"].includes(location.pathname) ? "text-crestora-gold" : "text-cream hover:text-crestora-gold"}`} aria-haspopup="true" aria-expanded={desktopDropdown === "about"}>
                  TENTANG <ChevronDown size={12} aria-hidden="true" />
                </button>
                <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${desktopDropdown === "about" ? "opacity-100 visible" : "opacity-0 invisible"}`}>
                  <div className="bg-espresso border border-cream/20 rounded-lg py-2 min-w-[180px] shadow-elevated">
                    <Link to="/cerita-kami" className="block px-5 py-2.5 text-cream hover:text-crestora-gold text-[13px] tracking-wider uppercase transition-colors">TENTANG KAMI</Link>
                    <Link to="/faq" className="block px-5 py-2.5 text-cream hover:text-crestora-gold text-[13px] tracking-wider uppercase transition-colors">FAQ</Link>
                  </div>
                </div>
              </div>

              <Link to="/toko-kopi" className={`font-sans text-[13px] tracking-[0.2em] uppercase transition-colors ${location.pathname === "/toko-kopi" ? "text-crestora-gold" : "text-cream hover:text-crestora-gold"}`}>
                KOPI
              </Link>

              <div className="relative group" onMouseEnter={() => setDesktopDropdown("support")} onMouseLeave={() => setDesktopDropdown(null)}>
                <button className={`font-sans text-[13px] tracking-[0.2em] uppercase transition-colors flex items-center gap-1 ${["/solusi-kantor", "/mesin-kopi-sewa"].includes(location.pathname) ? "text-crestora-gold" : "text-cream hover:text-crestora-gold"}`} aria-haspopup="true" aria-expanded={desktopDropdown === "support"}>
                  PENDUKUNG <ChevronDown size={12} aria-hidden="true" />
                </button>
                <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${desktopDropdown === "support" ? "opacity-100 visible" : "opacity-0 invisible"}`}>
                  <div className="bg-espresso border border-cream/20 rounded-lg py-2 min-w-[240px] shadow-elevated">
                    <Link to="/solusi-kantor" className="block px-5 py-2.5 text-cream hover:text-crestora-gold text-[13px] tracking-wider uppercase transition-colors">MESIN KOPI JUAL</Link>
                    <Link to="/mesin-kopi-sewa" className="block px-5 py-2.5 text-cream hover:text-crestora-gold text-[13px] tracking-wider uppercase transition-colors">MESIN KOPI SEWA</Link>
                  </div>
                </div>
              </div>

              <div className="relative group" onMouseEnter={() => setDesktopDropdown("solution")} onMouseLeave={() => setDesktopDropdown(null)}>
                <button className={`font-sans text-[13px] tracking-[0.2em] uppercase transition-colors flex items-center gap-1 ${["/roastery", "/coffee-shop-starter"].includes(location.pathname) ? "text-crestora-gold" : "text-cream hover:text-crestora-gold"}`} aria-haspopup="true" aria-expanded={desktopDropdown === "solution"}>
                  SOLUSI <ChevronDown size={12} aria-hidden="true" />
                </button>
                <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${desktopDropdown === "solution" ? "opacity-100 visible" : "opacity-0 invisible"}`}>
                  <div className="bg-espresso border border-cream/20 rounded-lg py-2 min-w-[220px] shadow-elevated">
                    <Link to="/roastery" className="block px-5 py-2.5 text-cream hover:text-crestora-gold text-[13px] tracking-wider uppercase transition-colors">ROASTERY</Link>
                    <Link to="/coffee-shop-starter" className="block px-5 py-2.5 text-cream hover:text-crestora-gold text-[13px] tracking-wider uppercase transition-colors">COFFEE SHOP STARTER</Link>
                  </div>
                </div>
              </div>

              <Link to="/hubungi-kami" className={`font-sans text-[13px] tracking-[0.2em] uppercase transition-colors ${location.pathname === "/hubungi-kami" ? "text-crestora-gold" : "text-cream hover:text-crestora-gold"}`}>
                KONTAK
              </Link>
            </div>

            <button
              className="lg:hidden text-cream p-3 min-w-[48px] min-h-[48px] flex items-center justify-center"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Tutup menu" : "Buka menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
        </div>
      </header>

      {/* Menu Mobile */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-espresso flex flex-col animate-fade-in" role="dialog" aria-modal="true" aria-label="Menu navigasi">
          <div className="flex items-center justify-between px-5 h-16 flex-shrink-0">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} aria-label="Beranda">
              <img src={crestoraLogo} alt="Logo Crestora Coffee Roasters" className="h-10 w-auto" width={40} height={40} />
            </Link>
            <button
              className="text-cream p-3 min-w-[48px] min-h-[48px] flex items-center justify-center"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Tutup menu"
            >
              <X size={22} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-5 pt-6" aria-label="Menu mobile">
            <div className="flex flex-col">
              <Link to="/" className={`py-3 min-h-[48px] flex items-center font-display text-base font-bold tracking-wider uppercase ${location.pathname === "/" ? "text-crestora-gold" : "text-cream"}`}>
                BERANDA
              </Link>

              <div>
                <button onClick={() => toggleDropdown("about")} className="w-full py-3 min-h-[48px] flex items-center gap-2 text-cream font-display text-base font-bold tracking-wider uppercase" aria-expanded={openDropdown === "about"}>
                  TENTANG
                  <ChevronDown size={14} className={`text-cream transition-transform ${openDropdown === "about" ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {openDropdown === "about" && (
                  <div className="pb-2 pl-4 space-y-1 animate-fade-in">
                    <Link to="/cerita-kami" className="text-cream text-sm tracking-wider uppercase hover:text-crestora-gold transition-colors min-h-[48px] flex items-center">TENTANG KAMI</Link>
                    <Link to="/faq" className="text-cream text-sm tracking-wider uppercase hover:text-crestora-gold transition-colors min-h-[48px] flex items-center">FAQ</Link>
                  </div>
                )}
              </div>

              <Link to="/toko-kopi" className={`py-3 min-h-[48px] flex items-center font-display text-base font-bold tracking-wider uppercase ${location.pathname === "/toko-kopi" ? "text-crestora-gold" : "text-cream"}`}>
                KOPI
              </Link>

              <div>
                <button onClick={() => toggleDropdown("supporting")} className="w-full py-3 min-h-[48px] flex items-center gap-2 text-cream font-display text-base font-bold tracking-wider uppercase" aria-expanded={openDropdown === "supporting"}>
                  PENDUKUNG
                  <ChevronDown size={14} className={`text-cream transition-transform ${openDropdown === "supporting" ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {openDropdown === "supporting" && (
                  <div className="pb-2 pl-4 space-y-1 animate-fade-in">
                    <Link to="/solusi-kantor" className="text-cream text-sm tracking-wider uppercase hover:text-crestora-gold transition-colors min-h-[48px] flex items-center">MESIN KOPI JUAL</Link>
                    <Link to="/mesin-kopi-sewa" className="text-cream text-sm tracking-wider uppercase hover:text-crestora-gold transition-colors min-h-[48px] flex items-center">MESIN KOPI SEWA</Link>
                  </div>
                )}
              </div>

              <div>
                <button onClick={() => toggleDropdown("solution")} className="w-full py-3 min-h-[48px] flex items-center gap-2 text-cream font-display text-base font-bold tracking-wider uppercase" aria-expanded={openDropdown === "solution"}>
                  SOLUSI
                  <ChevronDown size={14} className={`text-cream transition-transform ${openDropdown === "solution" ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {openDropdown === "solution" && (
                  <div className="pb-2 pl-4 space-y-1 animate-fade-in">
                    <Link to="/roastery" className="text-cream text-sm tracking-wider uppercase hover:text-crestora-gold transition-colors min-h-[48px] flex items-center">ROASTERY</Link>
                    <Link to="/coffee-shop-starter" className="text-cream text-sm tracking-wider uppercase hover:text-crestora-gold transition-colors min-h-[48px] flex items-center">COFFEE SHOP STARTER</Link>
                  </div>
                )}
              </div>

              <Link to="/hubungi-kami" className="py-3 min-h-[48px] flex items-center text-cream font-display text-base font-bold tracking-wider uppercase">
                KONTAK
              </Link>
            </div>

            <div className="mt-10 pt-6">
              <ul className="flex items-center gap-3 mb-5 list-none" aria-label="Media sosial">
                <li>
                  <a href="https://instagram.com/CrestoraCoffee" target="_blank" rel="noopener noreferrer" className="text-cream hover:text-crestora-gold transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center" aria-label="Instagram Crestora Coffee">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                </li>
                <li>
                  <a href="https://youtube.com/@CrestoraCoffee" target="_blank" rel="noopener noreferrer" className="text-cream hover:text-crestora-gold transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center" aria-label="YouTube Crestora Coffee">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
                  </a>
                </li>
              </ul>
              <div className="space-y-2 text-cream text-sm">
                <p className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-crestora-gold" aria-hidden="true" />
                  <span><span className="text-crestora-gold">JAM KERJA :</span> 08:00 - 21:00</span>
                </p>
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-crestora-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><span className="text-crestora-gold">ALAMAT :</span> Jl. Seturan Raya No.17A, Condongcatur, Sleman, Yogyakarta</span>
                </p>
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Navbar;
